// src/utils/anomalyDetector.js
// Detect anomalies in transactions based on category budgets and daily limits

export function detectAnomalies(transactions, categoryBudgets = {}, dailyBudget = 0) {
    if (!transactions || transactions.length === 0) return [];

    const alerts = [];

    // Compute category averages from all debit transactions
    const categoryTotals = {};
    const categoryCounts = {};
    const dailyTotals = {};

    transactions.forEach(t => {
        const isDebit = t.type === 'debit' || t.type === 'expense';
        if (!isDebit) return;
        const amt = Math.abs(t.amount || 0);
        const cat = t.category || 'Other';
        const day = (t.date || '').slice(0, 10);

        categoryTotals[cat] = (categoryTotals[cat] || 0) + amt;
        categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;

        if (day) {
            dailyTotals[day] = (dailyTotals[day] || 0) + amt;
        }
    });

    const categoryAvg = {};
    Object.keys(categoryTotals).forEach(cat => {
        categoryAvg[cat] = categoryCounts[cat] > 0 ? categoryTotals[cat] / categoryCounts[cat] : 0;
    });

    // Rule 1: Single transaction > 3x category average
    transactions.forEach(t => {
        const isDebit = t.type === 'debit' || t.type === 'expense';
        if (!isDebit) return;
        const amt = Math.abs(t.amount || 0);
        const cat = t.category || 'Other';
        const avg = categoryAvg[cat] || 0;
        if (avg > 0 && amt > avg * 3) {
            alerts.push({
                type: 'spike',
                severity: 'high',
                message: `₹${amt.toLocaleString()} on "${t.description || cat}" is ${(amt / avg).toFixed(1)}x the avg for ${cat}`,
                date: t.date,
                amount: amt,
            });
        }
    });

    // Rule 2: Daily spend > 2x daily budget
    if (dailyBudget > 0) {
        Object.entries(dailyTotals).forEach(([day, total]) => {
            if (total > dailyBudget * 2) {
                alerts.push({
                    type: 'daily_breach',
                    severity: 'medium',
                    message: `₹${total.toLocaleString()} spent on ${day} — ${(total / dailyBudget).toFixed(1)}x daily budget`,
                    date: day,
                    amount: total,
                });
            }
        });
    }

    // Rule 3: Category budget exceeded
    Object.entries(categoryBudgets).forEach(([cat, budget]) => {
        const spent = categoryTotals[cat] || 0;
        if (budget > 0 && spent > budget) {
            alerts.push({
                type: 'category_breach',
                severity: 'high',
                message: `${cat} spending ₹${spent.toLocaleString()} exceeds budget of ₹${budget.toLocaleString()}`,
                amount: spent,
            });
        }
    });

    return alerts;
}
